import searchIcon from '@/assets/copilot/search.svg'
import { useUiStrings } from '@/hooks/useUiStrings'
import { answerCardToSuggestion } from './assistAnswerAdapter'
import type { AssistAskInput, AssistSuggestion, NestedAssistAnswer } from './assist.types'
import { isAnswerExpanded, resolveSuggestionTag } from './assist.types'
import AssistAnswerBody from './AssistAnswerBody'
import AssistTagPill from './AssistTagPill'

type AssistSuggestionRowProps = {
  suggestion: AssistSuggestion
  answer: NestedAssistAnswer | undefined
  expandedCardIds: string[]
  disabled: boolean
  onAsk: (input: AssistAskInput) => void
  onToggleAnswer: (rowKey: string) => void
}

/**
 * One suggested question. Once answered it becomes the design's active card — a
 * blue 2px border with the answer kept inside it — and toggles that answer
 * instead of asking the same question twice.
 */
export default function AssistSuggestionRow({
  suggestion,
  answer,
  expandedCardIds,
  disabled,
  onAsk,
  onToggleAnswer,
}: AssistSuggestionRowProps) {
  const { t } = useUiStrings()
  const tagPill = resolveSuggestionTag(suggestion)
  // Locale labels come from the app; the AI's own labels are shown as sent.
  const tagLabel = tagPill.kind === 'locale' ? t[tagPill.labelKey] : tagPill.label
  const expanded = isAnswerExpanded(answer, expandedCardIds)

  const questionRow = (
    <span className="flex min-w-0 flex-1 items-center gap-2 pr-2">
      <img
        src={searchIcon}
        alt=""
        width={14}
        height={14}
        className="block h-[14px] w-[14px] shrink-0"
      />
      <span
        className={[
          // `text-xs` is the panel's own base size, used sixteen times across
          // it. This row and the quote above it had opted down to 11px, which
          // is what made the list hard to read at a glance mid-meeting.
          'min-w-0 break-words text-xs leading-4',
          // Active card switches the label from semibold slate to medium ink.
          answer
            ? 'font-medium text-[#0f172a] dark:text-[var(--text-primary)]'
            : 'font-semibold text-[#334155] dark:text-[var(--text-primary)]',
        ].join(' ')}
      >
        {suggestion.text}
      </span>
    </span>
  )

  if (answer) {
    return (
      <div
        className="rounded-lg border-2 border-[#3b82f6] bg-white p-[12px] shadow-[0px_1px_1px_rgba(0,0,0,0.05)] dark:bg-[var(--bg-tertiary)]"
        data-testid={`copilot-suggestion-card-${suggestion.id}`}
        data-active="true"
      >
        {/* Deliberately never disabled, unlike the ask button below. Opening
            an answer that has already arrived sends nothing — it is reading,
            not asking — and disabling it with the rest of the list meant that
            while one question was in flight the reader could not look at any
            answer they already had. */}
        <button
          type="button"
          onClick={() => onToggleAnswer(answer.rowKey)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-between text-left"
          data-testid={`copilot-suggestion-${suggestion.id}`}
        >
          {questionRow}
          <AssistTagPill label={tagLabel} tone={tagPill.tone} />
        </button>
        {expanded ? (
          <>
            {/* Figma puts this rule 6.5px under the question row and the answer
                13.5px under it: the 20px row gap split by the 1px rule. */}
            <div className="mt-[6.5px] border-b border-[#e4effe]" />
            <div className="mt-[13.5px]">
              <AssistAnswerBody
                suggestion={answerCardToSuggestion(answer.card, answer.rowKey, t)}
              />
            </div>
          </>
        ) : null}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => onAsk({ suggestionId: suggestion.id })}
      disabled={disabled}
      className="flex w-full items-center justify-between rounded-lg border border-[rgba(226,232,240,0.8)] bg-white p-[11px] text-left shadow-[0px_1px_1px_rgba(0,0,0,0.05)] transition-colors hover:bg-slate-50 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-white dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)] dark:hover:bg-white/5 dark:disabled:hover:bg-[var(--bg-tertiary)]"
      data-testid={`copilot-suggestion-${suggestion.id}`}
      data-active="false"
    >
      {questionRow}
      <AssistTagPill label={tagLabel} tone={tagPill.tone} />
    </button>
  )
}
